import { IonCard, IonCardContent, IonIcon } from '@ionic/react';
import { addOutline, sparklesOutline } from 'ionicons/icons';
import React, { useCallback, useRef } from 'react';
import { cancelRun } from '../../services/AgentChat.service';
import { AgentChatImageAttachment } from './AgentChat.types';
import AgentChatComposer, { AgentComposerHandle } from './AgentChatComposer';
import AgentChatEmpty from './AgentChatEmpty';
import AgentChatHistory from './AgentChatHistory';
import { useAgentChatSession } from './AgentChatSession';
import AgentChatSources from './AgentChatSources';
import AgentChatTabs from './AgentChatTabs';
import AgentChatThread from './AgentChatThread';
import { NEW_CHAT_DRAFT_KEY } from './agentComposerDrafts';
import { isDraftTabId } from './agentChatDraftTab';
import './AgentChat.scss';
import './AgentChatHomepage.scss';

export const AgentChatWidget: React.FC<{ editing: boolean }> = ({
  editing,
}) => {
  const {
    runs,
    openTabs,
    activeRun,
    activeTabId,
    configured,
    viewingHistory,
    toggleHistory,
    openRun,
    closeTab,
    newChat,
    clearHistory,
    send,
    selectedEngine,
  } = useAgentChatSession();

  const composerRef = useRef<AgentComposerHandle>(null);

  const startRun = useCallback(
    (prompt: string, images?: AgentChatImageAttachment[]) => {
      send(prompt, activeTabId ?? undefined, images);
    },
    [send, activeTabId],
  );

  const submitPrompt = useCallback(
    (prompt: string, images?: AgentChatImageAttachment[]) => {
      if (!prompt.trim()) return;
      startRun(prompt, images);
    },
    [startRun],
  );

  const isActiveRunning =
    activeRun?.status === 'running' || activeRun?.status === 'queued';
  const hasActiveMessages = (activeRun?.messages.length ?? 0) > 0;
  const isNewChatSlate = activeTabId === null && !viewingHistory;
  const isDraftTab = Boolean(activeTabId && isDraftTabId(activeTabId));
  const chatState: 'empty' | 'history' | 'active' =
    runs.length === 0 || isDraftTab || (isNewChatSlate && openTabs.length > 0)
      ? 'empty'
      : hasActiveMessages
        ? 'active'
        : 'history';

  return (
    <IonCard className="modern-widget-card agent-chat-card">
      <IonCardContent className="modern-widget-content agent-chat-content">
        <div className="agent-chat-main">
          <div
            className={`agent-chat-chrome${
              openTabs.length > 0 ? ' has-tabs' : ''
            }`}
          >
            <div className="agent-chat-header">
              <IonIcon
                icon={sparklesOutline}
                className="agent-chat-header-icon"
              />
              <h3 className="widget-title">Ask Crate Hackers</h3>
              <div className="agent-chat-header-actions">
                {runs.length > 0 && (
                  <button
                    type="button"
                    className={`agent-history-pill${
                      viewingHistory ? ' is-open' : ''
                    }`}
                    disabled={editing}
                    onClick={toggleHistory}
                  >
                    History {runs.length}
                  </button>
                )}
                <button
                  type="button"
                  className="agent-new-chat"
                  disabled={editing}
                  onClick={newChat}
                >
                  <IonIcon icon={addOutline} />
                  New
                </button>
              </div>
            </div>

            {openTabs.length > 0 && (
              <div className="agent-chat-tabbar">
                <AgentChatTabs
                  tabs={openTabs}
                  activeId={activeTabId}
                  onSelect={openRun}
                  onClose={closeTab}
                  onNewChat={newChat}
                  disabled={editing}
                />
              </div>
            )}
          </div>

          <div className="agent-chat-body">
            <div className="agent-chat-middle">
              <div className="agent-chat-scrim" />
              {chatState === 'empty' && (
                <AgentChatEmpty
                  configured={configured}
                  engine={selectedEngine}
                  onPrompt={submitPrompt}
                />
              )}
              {(chatState === 'history' || viewingHistory) &&
                chatState !== 'empty' && (
                  <AgentChatHistory
                    runs={runs}
                    activeRunId={activeRun?.id ?? null}
                    onOpen={openRun}
                  />
                )}
              {chatState === 'active' && !viewingHistory && (
                <AgentChatThread
                  run={activeRun}
                  configured={configured}
                  engine={selectedEngine}
                  onRerunPrompt={
                    editing
                      ? undefined
                      : (prompt) => {
                          const conversationId =
                            activeTabId && !isDraftTabId(activeTabId)
                              ? activeTabId
                              : activeRun?.id;
                          send(prompt, conversationId);
                        }
                  }
                />
              )}
            </div>

            <AgentChatComposer
              ref={composerRef}
              draftKey={activeTabId ?? NEW_CHAT_DRAFT_KEY}
              onSubmit={submitPrompt}
              onStop={() => activeRun && cancelRun(activeRun.id)}
              isRunning={isActiveRunning}
              showSuggestions={chatState === 'history' && !viewingHistory}
              disabled={editing}
              placeholder={
                chatState === 'active'
                  ? 'Ask a follow-up in this conversation…'
                  : 'Ask about your library…'
              }
              sourcesBar={
                <AgentChatSources
                  disabled={editing}
                  onInsertTrack={(track) =>
                    composerRef.current?.insertTrack(track)
                  }
                />
              }
              modelPicker={
                <div className="agent-composer-model-row">
                  {runs.length > 0 && (
                    <button
                      type="button"
                      className="agent-clear-history"
                      disabled={editing || isActiveRunning}
                      onClick={() => void clearHistory()}
                      title="Clear chat history"
                    >
                      Clear
                    </button>
                  )}
                </div>
              }
            />
          </div>
        </div>
      </IonCardContent>
    </IonCard>
  );
};

export default AgentChatWidget;
